import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, User, Globe, LogOut, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import LumzaLogo from './LumzaLogo';
import MobileNavDrawer from './MobileNavDrawer';
import HeaderChatButton from './HeaderChatButton';
import LocationPicker from './LocationPicker';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSearch } from '@/contexts/SearchContext';
import { useAuth } from '@/contexts/AuthContext';
import { usePremiumContext } from '@/contexts/PremiumContext';
import { z } from 'zod';

const searchSchema = z.string().trim().max(100);

const Header = () => {
  const { currentLanguage, setLanguage, t } = useLanguage();
  const { setSearchQuery } = useSearch();
  const { user, hasRole, signOut } = useAuth();
  const { isPremium } = usePremiumContext();
  const navigate = useNavigate();
  const [localQuery, setLocalQuery] = useState('');
  
  const languages = [
    { code: 'EN' as const, name: 'English', flag: '🇬🇧' },
    { code: 'ZU' as const, name: 'Zulu', flag: '🇿🇦' },
    { code: 'XH' as const, name: 'Xhosa', flag: '🇿🇦' },
    { code: 'ZH' as const, name: '中文', flag: '🇨🇳' }
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const validated = searchSchema.parse(localQuery);
      setSearchQuery(validated);
      navigate(`/search?q=${encodeURIComponent(validated)}`);
    } catch (error) {
      console.error('Invalid search input');
    }
  };

  return (
    <header
      className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border/40"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Mobile Menu + Logo */}
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <MobileNavDrawer 
              currentLanguage={currentLanguage}
              onLanguageChange={setLanguage}
            />
            <Link to="/" className="flex min-w-0">
              <LumzaLogo size="sm" showTagline={false} />
            </Link>
          </div>

          {/* Desktop Search Bar */}
          <form onSubmit={handleSearch} className="hidden lg:flex items-center flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                value={localQuery}
                onChange={(e) => setLocalQuery(e.target.value)}
                placeholder={t('header.search.placeholder')}
                className="search-premium pl-10"
                maxLength={100}
              />
            </div>
          </form>

          {/* Right Side Controls */}
          <div className="flex items-center space-x-1 sm:space-x-2">
            {/* Location - Desktop Only */}
            <div className="hidden md:block">
              <LocationPicker />
            </div>

            {/* Language Toggle - Desktop Only */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="hidden md:flex items-center space-x-1"
                >
                  <Globe className="h-4 w-4" />
                  <span className="text-sm">{currentLanguage}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-44 bg-popover border border-border shadow-lg z-50"
              >
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className="flex items-center space-x-2 cursor-pointer hover:bg-accent"
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.name}</span>
                    {currentLanguage === lang.code && (
                      <span className="ml-auto text-primary">✓</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* WhatsApp Chat + Profile - All Devices */}
            <HeaderChatButton />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-1 p-2 touch-target">
                    <User className="h-4 w-4" />
                    {isPremium && <Crown className="h-3 w-3 text-neon-yellow" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 z-50 bg-popover border border-border">
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <User className="h-4 w-4 mr-2" />
                    Profile
                    {isPremium && <Crown className="h-3 w-3 ml-auto text-neon-yellow" />}
                  </DropdownMenuItem>
                  {hasRole('vendor') && (
                    <DropdownMenuItem onClick={() => navigate('/vendor/dashboard')} className="cursor-pointer">
                      Vendor Dashboard
                    </DropdownMenuItem>
                  )}
                  {hasRole('driver') && (
                    <DropdownMenuItem onClick={() => navigate('/driver/dashboard')} className="cursor-pointer">
                      Driver Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/onboard/vendor')} className="cursor-pointer">
                    Become a Vendor
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/onboard/driver')} className="cursor-pointer">
                    Become a Driver
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={signOut} className="text-destructive cursor-pointer">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth" aria-label="Sign in or create account">
                <Button variant="ghost" size="sm" className="flex items-center p-2 touch-target" aria-label="Sign in">
                  <User className="h-4 w-4" />
                </Button>
              </Link>
            )}

            {/* Order Now CTA - Desktop Only */}
            <Button 
              className="btn-neon hidden lg:flex"
              onClick={() => {
                const storesSection = document.querySelector('#stores-section');
                if (storesSection) {
                  storesSection.scrollIntoView({ behavior: 'smooth' });
                } else {
                  navigate('/');
                }
              }}
            >
              {t('header.orderNow')}
            </Button>
          </div>
        </div>

        {/* Mobile Search */}
        <form onSubmit={handleSearch} className="lg:hidden pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              value={localQuery}
              onChange={(e) => setLocalQuery(e.target.value)}
              placeholder={t('header.search.placeholder')}
              className="search-premium pl-10 h-12 text-base"
              maxLength={100}
            />
          </div>
        </form>
      </div>
    </header>
  );
};

export default Header;